/**
 * Sections d’annonces situées avant le header (ordre document), sans dépendre d’un wrapper de section group
 * (Shopify rend souvent les sections du groupe comme frères successifs dans le layout).
 */
const getAnnouncementSectionsBeforeHeader = () => {
  const headerSection = document.querySelector('.shopify-section.section-header');
  const sections = [...document.querySelectorAll('.shopify-section.section-announcement-bar')];

  return sections.filter((section) => {
    if (!headerSection) return true;

    return (headerSection.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_PRECEDING) !== 0;
  });
};

/**
 * Empile les barres sticky (`--announcement-sticky-top` sur le wrapper `#shopify-section-*`) et met à jour `--announcement-bar-height`.
 */
const updateStickyAnnouncementStack = () => {
  for (const sectionEl of document.querySelectorAll('.shopify-section.section-announcement-bar')) {
    sectionEl.style.removeProperty('--announcement-sticky-top');
  }

  let cumulativeTop = 0;
  let totalStickyHeight = 0;

  for (const sectionEl of getAnnouncementSectionsBeforeHeader()) {
    const stickyInner = sectionEl.querySelector('.announcement-bar.announcement-bar--sticky');
    if (!stickyInner) continue;

    sectionEl.style.setProperty('--announcement-sticky-top', `${cumulativeTop}px`);
    cumulativeTop += sectionEl.offsetHeight;
    totalStickyHeight += sectionEl.offsetHeight;
  }

  document.documentElement.style.setProperty('--announcement-bar-height', `${totalStickyHeight}px`);
};

class HeaderComponent extends HTMLElement {
  #initialHeaderTop = 0;
  #scrollHandler = null;
  #resizeObserver = null;
  #announcementResizeObserver = null;

  constructor() {
    super();

    this.menuToggle = null;
    this.menuClose = null;
    this.mobileMenuOverlay = null;
    this.mobileMenu = null;
  }

  connectedCallback() {
    this.menuToggle = this.querySelector('[data-ref="menu-toggle"]');
    this.menuClose = this.querySelector('[data-ref="menu-close"]');
    this.mobileMenuOverlay = this.querySelector('[data-ref="mobile-menu-overlay"]');
    this.mobileMenu = this.querySelector('[data-ref="mobile-menu"]');

    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', this.#handleMenuOpen);
    }

    if (this.menuClose) {
      this.menuClose.addEventListener('click', this.#handleMenuClose);
    }

    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.addEventListener('click', this.#handleOverlayClick);
    }

    document.addEventListener('keydown', this.#handleKeyDown);

    // Initialiser l'observation sticky si le header est sticky
    this.#initStickyObserver();

    this.updateHeaderOffsetHeight();
    this.#resizeObserver = new ResizeObserver(() => this.updateHeaderOffsetHeight());
    this.#resizeObserver.observe(this);
    this.#observeStickyAnnouncementSections();
  }

  disconnectedCallback() {
    if (this.menuToggle) {
      this.menuToggle.removeEventListener('click', this.#handleMenuOpen);
    }

    if (this.menuClose) {
      this.menuClose.removeEventListener('click', this.#handleMenuClose);
    }

    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.removeEventListener('click', this.#handleOverlayClick);
    }

    document.removeEventListener('keydown', this.#handleKeyDown);

    // Nettoyer l'observer
    this.#cleanupStickyObserver();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#announcementResizeObserver) {
      this.#announcementResizeObserver.disconnect();
      this.#announcementResizeObserver = null;
    }
  }

  #handleMenuOpen = () => {
    this.openMobileMenu();
  };

  #handleMenuClose = () => {
    this.closeMobileMenu();
  };

  #handleOverlayClick = (event) => {
    // Fermer le menu si on clique sur l'overlay (pas sur le menu lui-même)
    if (event.target === this.mobileMenuOverlay) {
      this.closeMobileMenu();
    }
  };

  #handleKeyDown = (event) => {
    // Fermer le menu avec la touche Escape
    if (event.key === 'Escape' && this.mobileMenuOverlay.classList.contains('is-open')) {
      this.closeMobileMenu();
    }
  };

  openMobileMenu() {
    // Nettoyer les classes pour éviter les conflits
    this.mobileMenuOverlay.classList.remove('is-closing');
    this.mobileMenuOverlay.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    // Focus sur le bouton de fermeture pour l'accessibilité
    setTimeout(() => {
      this.menuClose?.focus();
    }, 150);
  }

  closeMobileMenu() {
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.mobileMenuOverlay.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer l'overlay
    setTimeout(() => {
      this.mobileMenuOverlay.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');

      // Remettre le focus sur le bouton menu
      this.menuToggle?.focus();
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  /**
   * Met à jour la hauteur de l'offset du header, utile pour la position top des megamenus.
   */
  updateHeaderOffsetHeight = () => {
    this.style.setProperty('--header-offset-height', `${this.offsetHeight}px`);
    document.documentElement.style.setProperty('--header-offset-height', `${this.offsetHeight}px`);
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    updateStickyAnnouncementStack();

    let headerBottomPosition = this.offsetHeight;
    document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => {
      if (section === this.closest('.shopify-section-group-header-group')) {
        return;
      }
      headerBottomPosition += section.offsetHeight;
    });
    document.documentElement.style.setProperty('--header-bottom-position', `${headerBottomPosition}px`);
  };

  #observeStickyAnnouncementSections() {
    if (this.#announcementResizeObserver) {
      this.#announcementResizeObserver.disconnect();
    }

    this.#announcementResizeObserver = new ResizeObserver(() => this.updateHeaderOffsetHeight());

    for (const sectionEl of getAnnouncementSectionsBeforeHeader()) {
      this.#announcementResizeObserver.observe(sectionEl);
    }
  }

  /**
   * Initialise l'observation du header pour détecter le scroll
   * (état sticky + sortie du mode transparent en haut de page).
   */
  #initStickyObserver() {
    const headerSection = this.closest('.section-header');
    const isSectionSticky = headerSection && getComputedStyle(headerSection).position === 'sticky';
    const isTransparent =
      this.classList.contains('header--transparent') || this.classList.contains('header--transparent-mobile');

    // Sticky est sur `.section-header`, pas sur le groupe ; le transparent a aussi besoin du scroll
    if (!isSectionSticky && !isTransparent) return;

    // Transparent uniquement tout en haut (y compris avec barre d'annonce sticky)
    this.#initialHeaderTop = 20;

    this.#scrollHandler = this.#throttle(() => {
      this.#checkStickyState();
    }, 10);

    window.addEventListener('scroll', this.#scrollHandler, { passive: true });
    this.#checkStickyState();
  }

  /**
   * Vérifie l'état sticky / transparent du header
   */
  #checkStickyState() {
    if (this.#initialHeaderTop === null) return;

    const isSticky = window.scrollY >= this.#initialHeaderTop;
    this.dataset.stickyState = isSticky ? 'active' : 'inactive';
  }

  /**
   * Throttle function pour optimiser les performances
   */
  #throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  /**
   * Nettoie l'observer lors de la destruction du composant
   */
  #cleanupStickyObserver() {
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
    this.#initialHeaderTop = null;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

document.addEventListener('shopify:section:load', () => {
  updateStickyAnnouncementStack();
  document.querySelector('header-component')?.updateHeaderOffsetHeight?.();
});

document.addEventListener('shopify:section:unload', () => {
  updateStickyAnnouncementStack();
  document.querySelector('header-component')?.updateHeaderOffsetHeight?.();
});
